/**
 * Which liquidity sources are live.
 *
 * 1inch is the default. It needs an API key, held by the proxy - see server/.
 *
 * OpenOcean was the original choice precisely because it needed no key, but its
 * public endpoint returns HTTP 403 behind Cloudflare bot protection for every
 * non-interactive client: from a browser on our own origin, from an honestly
 * identified server, and from an anonymous one. Getting past that would mean
 * disguising the client as a browser, which is bot-detection evasion and would break
 * the moment they tightened it. Its provider is kept and tested, so it can be
 * re-enabled here if OpenOcean grants proper API access.
 *
 * Switching or adding a source is a config change, not a code change.
 */

export const DEFAULT_ENABLED_PROVIDERS = ['1inch']

/** Every provider implemented in ./providers, whether or not it is enabled. */
export const KNOWN_PROVIDER_IDS = ['openocean', '1inch', '0x']

export function parseEnabledProviders(raw: string | undefined): string[] {
  if (!raw || !raw.trim()) return DEFAULT_ENABLED_PROVIDERS

  const requested = raw
    .split(',')
    .map((id) => id.trim().toLowerCase())
    .filter(Boolean)

  const known = requested.filter((id) => KNOWN_PROVIDER_IDS.includes(id))
  const unknown = requested.filter((id) => !KNOWN_PROVIDER_IDS.includes(id))

  if (unknown.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[aggregator] Ignoring unknown quote provider(s): ${unknown.join(', ')}. ` +
        `Known providers: ${KNOWN_PROVIDER_IDS.join(', ')}.`
    )
  }

  // An entirely invalid list would otherwise leave the app with no routing at all,
  // which is a worse failure than ignoring the misconfiguration.
  if (!known.length) {
    // eslint-disable-next-line no-console
    console.warn('[aggregator] No valid quote providers configured; falling back to the default.')
    return DEFAULT_ENABLED_PROVIDERS
  }

  return known
}

export const enabledProviderIds = parseEnabledProviders(process.env.REACT_APP_QUOTE_PROVIDERS)

export function isProviderEnabled(id: string): boolean {
  return enabledProviderIds.includes(id)
}
